/**
 * VoiceAvatar — Animated SVG face avatars with emotional expression & altered state effects.
 *
 * Pure SVG + CSS animations. No external libraries.
 * Each avatar is unique based on voice color. Emotions driven by SSE events.
 *
 * Props:
 *   name       - voice name (used for initial + seed)
 *   color      - voice color hex
 *   emotion    - 'idle' | 'speaking' | 'thinking' | 'arguing' | 'agreeing' | 'confused'
 *   alteredState - null | 'mushrooms' | 'weed' | 'drunk' | 'wired' | 'acid' | 'microdose'
 *   size       - pixel size (default 40)
 */
import React, { useMemo } from 'react';

// Deterministic seed from name for avatar variation
function hashName(name) {
  let h = 0;
  for (let i = 0; i < (name || '').length; i++) {
    h = ((h << 5) - h + name.charCodeAt(i)) | 0;
  }
  return Math.abs(h);
}

// Altered state CSS filter + overlay styles
const ALTERED_EFFECTS = {
  mushrooms: {
    filter: 'hue-rotate(20deg) saturate(1.4)',
    pupilScale: 2.2,
    blinkSpeed: '4s',
    extraClass: 'avatar-mushrooms',
  },
  weed: {
    filter: 'saturate(0.8) brightness(0.95)',
    pupilScale: 1.1,
    blinkSpeed: '5s',
    lidDroop: 0.35,
    extraClass: 'avatar-weed',
  },
  drunk: {
    filter: 'saturate(1.1)',
    pupilScale: 1.0,
    blinkSpeed: '3s',
    extraClass: 'avatar-drunk',
  },
  wired: {
    filter: 'contrast(1.15) brightness(1.05)',
    pupilScale: 1.6,
    blinkSpeed: '0.8s',
    extraClass: 'avatar-wired',
  },
  acid: {
    filter: 'hue-rotate(0deg) saturate(1.8)',
    pupilScale: 2.5,
    blinkSpeed: '3s',
    extraClass: 'avatar-acid',
  },
  microdose: {
    filter: 'saturate(1.15) brightness(1.02)',
    pupilScale: 1.3,
    blinkSpeed: '2.8s',
    extraClass: 'avatar-microdose',
  },
};

export default function VoiceAvatar({ name = '', color = '#FF4D00', emotion = 'idle', alteredState = null, size = 40 }) {
  const seed = useMemo(() => hashName(name), [name]);
  const altered = alteredState ? ALTERED_EFFECTS[alteredState] : null;
  const uid = useMemo(() => `va-${seed}-${Math.random().toString(36).slice(2, 6)}`, [seed]);

  // Seed-based face variations
  const eyeSpacing = 7 + (seed % 3);         // 7-9
  const eyeY = 17 + (seed % 3) - 1;          // 16-18
  const mouthWidth = 5 + (seed % 4);          // 5-8
  const mouthY = 27 + (seed % 3) - 1;        // 26-28
  const headShape = seed % 3;                 // 0=round, 1=square-ish, 2=tall
  const browAngle = (seed % 5) - 2;           // -2 to 2

  // Pupil size (base + altered state)
  const basePupil = 1.8;
  const pupilR = altered?.pupilScale ? basePupil * (altered.pupilScale / 1.5) : basePupil;

  // Lid droop for weed
  const lidDroop = altered?.lidDroop || 0;

  // Emotion-driven mouth + brow
  const getMouth = () => {
    const hw = mouthWidth / 2;
    switch (emotion) {
      case 'speaking':
        return { d: `M${20 - hw},${mouthY} Q20,${mouthY + 5} ${20 + hw},${mouthY}`, animate: true };
      case 'arguing':
        return { d: `M${20 - hw},${mouthY - 1} Q20,${mouthY + 6} ${20 + hw},${mouthY - 1}`, animate: true };
      case 'agreeing':
        return { d: `M${20 - hw},${mouthY} Q20,${mouthY + 3} ${20 + hw},${mouthY}`, animate: false };
      case 'confused':
        return { d: `M${20 - hw},${mouthY + 1} Q20,${mouthY - 1} ${20 + hw},${mouthY + 2}`, animate: false };
      case 'thinking':
        return { d: `M${20 - hw + 1},${mouthY} L${20 + hw - 1},${mouthY}`, animate: false };
      default: // idle
        return { d: `M${20 - hw + 1},${mouthY} Q20,${mouthY + 2} ${20 + hw - 1},${mouthY}`, animate: false };
    }
  };

  const mouth = getMouth();
  const blinkDur = altered?.blinkSpeed || '3s';

  // Head path
  const headPath = headShape === 0
    ? `M20,4 C30,4 34,12 34,20 C34,30 28,36 20,36 C12,36 6,30 6,20 C6,12 10,4 20,4Z`
    : headShape === 1
    ? `M20,5 C29,5 33,10 33,18 C33,28 28,35 20,35 C12,35 7,28 7,18 C7,10 11,5 20,5Z`
    : `M20,3 C28,3 32,11 32,20 C32,31 27,37 20,37 C13,37 8,31 8,20 C8,11 12,3 20,3Z`;

  // Cheek flush for drunk
  const showFlush = alteredState === 'drunk';
  // Bloodshot for weed
  const showBloodshot = alteredState === 'weed';

  const isSpeakingAnim = emotion === 'speaking' || emotion === 'arguing';
  const isWired = alteredState === 'wired';

  return (
    <div
      className={`voice-avatar shrink-0 ${altered?.extraClass || ''} ${isSpeakingAnim ? 'avatar-speaking' : ''} ${emotion === 'thinking' ? 'avatar-thinking' : ''}`}
      style={{ width: size, height: size, filter: altered?.filter || 'none' }}
      title={`${name}${alteredState ? ` (${alteredState})` : ''}`}
      data-testid={`voice-avatar-${name.toLowerCase().replace(/[\s']/g, '-')}`}
    >
      <svg viewBox="0 0 40 40" width={size} height={size} xmlns="http://www.w3.org/2000/svg">
        <defs>
          {/* Blink animation */}
          <clipPath id={`${uid}-blink`}>
            <rect x="0" y="0" width="40" height="40">
              <animate
                attributeName="height"
                values={`40;40;40;${20 + lidDroop * 20};40;40;40`}
                dur={blinkDur}
                repeatCount="indefinite"
                keyTimes="0;0.42;0.44;0.48;0.52;0.54;1"
              />
            </rect>
          </clipPath>

          {/* Acid hue rotation */}
          {alteredState === 'acid' && (
            <filter id={`${uid}-acid`}>
              <feColorMatrix type="hueRotate" values="0">
                <animate attributeName="values" values="0;360" dur="4s" repeatCount="indefinite" />
              </feColorMatrix>
            </filter>
          )}
        </defs>

        {/* Breathing / sway animation group */}
        <g className="avatar-body">
          {/* Head */}
          <path d={headPath} fill={color + '18'} stroke={color} strokeWidth="1.5"
            filter={alteredState === 'acid' ? `url(#${uid}-acid)` : undefined} />

          {/* Drunk flush */}
          {showFlush && (
            <>
              <circle cx={20 - eyeSpacing - 1} cy={eyeY + 5} r="3.5" fill="#EF4444" opacity="0.15">
                <animate attributeName="opacity" values="0.1;0.2;0.1" dur="3s" repeatCount="indefinite" />
              </circle>
              <circle cx={20 + eyeSpacing + 1} cy={eyeY + 5} r="3.5" fill="#EF4444" opacity="0.15">
                <animate attributeName="opacity" values="0.1;0.2;0.1" dur="3s" repeatCount="indefinite" />
              </circle>
            </>
          )}

          {/* Eyes group with blink */}
          <g clipPath={`url(#${uid}-blink)`}>
            {/* Eye whites */}
            <ellipse cx={20 - eyeSpacing} cy={eyeY} rx="3.5" ry="3" fill="#F5F5F5" opacity="0.9" />
            <ellipse cx={20 + eyeSpacing} cy={eyeY} rx="3.5" ry="3" fill="#F5F5F5" opacity="0.9" />

            {/* Bloodshot veins for weed */}
            {showBloodshot && (
              <>
                <line x1={20 - eyeSpacing - 3} y1={eyeY - 1} x2={20 - eyeSpacing - 1} y2={eyeY} stroke="#EF4444" strokeWidth="0.3" opacity="0.6" />
                <line x1={20 - eyeSpacing + 3} y1={eyeY - 1.5} x2={20 - eyeSpacing + 1.5} y2={eyeY} stroke="#EF4444" strokeWidth="0.3" opacity="0.5" />
                <line x1={20 + eyeSpacing - 3} y1={eyeY - 1} x2={20 + eyeSpacing - 1} y2={eyeY} stroke="#EF4444" strokeWidth="0.3" opacity="0.6" />
                <line x1={20 + eyeSpacing + 3} y1={eyeY - 1.5} x2={20 + eyeSpacing + 1.5} y2={eyeY} stroke="#EF4444" strokeWidth="0.3" opacity="0.5" />
              </>
            )}

            {/* Irises */}
            <circle cx={20 - eyeSpacing} cy={eyeY} r="2.2" fill={color}>
              {alteredState === 'acid' && (
                <animate attributeName="fill" values={`${color};#A855F7;#22C55E;#EF4444;${color}`} dur="3s" repeatCount="indefinite" />
              )}
            </circle>
            <circle cx={20 + eyeSpacing} cy={eyeY} r="2.2" fill={color}>
              {alteredState === 'acid' && (
                <animate attributeName="fill" values={`${color};#22C55E;#EF4444;#A855F7;${color}`} dur="3s" repeatCount="indefinite" />
              )}
            </circle>

            {/* Pupils */}
            <circle cx={20 - eyeSpacing} cy={eyeY} r={pupilR} fill="#0A0A0A">
              {isWired && (
                <animate attributeName="r" values={`${pupilR};${pupilR * 0.8};${pupilR}`} dur="0.3s" repeatCount="indefinite" />
              )}
            </circle>
            <circle cx={20 + eyeSpacing} cy={eyeY} r={pupilR} fill="#0A0A0A">
              {isWired && (
                <animate attributeName="r" values={`${pupilR * 0.8};${pupilR};${pupilR * 0.8}`} dur="0.3s" repeatCount="indefinite" />
              )}
            </circle>

            {/* Pupil highlights */}
            <circle cx={20 - eyeSpacing + 0.8} cy={eyeY - 0.8} r="0.6" fill="#F5F5F5" opacity="0.7" />
            <circle cx={20 + eyeSpacing + 0.8} cy={eyeY - 0.8} r="0.6" fill="#F5F5F5" opacity="0.7" />

            {/* Weed heavy lids */}
            {alteredState === 'weed' && (
              <>
                <rect x={20 - eyeSpacing - 4} y={eyeY - 4} width="8" height={3 + lidDroop * 6} fill={color + '18'} rx="1" />
                <rect x={20 + eyeSpacing - 4} y={eyeY - 4} width="8" height={3 + lidDroop * 6} fill={color + '18'} rx="1" />
              </>
            )}
          </g>

          {/* Eyebrows */}
          <line
            x1={20 - eyeSpacing - 2.5} y1={eyeY - 5 - browAngle}
            x2={20 - eyeSpacing + 2.5} y2={eyeY - 5 + (emotion === 'arguing' ? -1 : emotion === 'confused' ? 1.5 : 0)}
            stroke={color} strokeWidth="1" strokeLinecap="round" opacity="0.6"
          />
          <line
            x1={20 + eyeSpacing - 2.5} y1={eyeY - 5 + (emotion === 'confused' ? -1 : 0)}
            x2={20 + eyeSpacing + 2.5} y2={eyeY - 5 + browAngle}
            stroke={color} strokeWidth="1" strokeLinecap="round" opacity="0.6"
          />

          {/* Mouth */}
          <path d={mouth.d} fill="none" stroke={color} strokeWidth="1.2" strokeLinecap="round" opacity="0.7">
            {mouth.animate && (
              <animate
                attributeName="d"
                values={`${mouth.d};M${20 - mouthWidth / 2},${mouthY} Q20,${mouthY + 2} ${20 + mouthWidth / 2},${mouthY};${mouth.d}`}
                dur="0.4s"
                repeatCount="indefinite"
              />
            )}
          </path>

          {/* Weed slight smile */}
          {alteredState === 'weed' && emotion === 'idle' && (
            <path d={`M${20 - mouthWidth / 2 + 1},${mouthY} Q20,${mouthY + 3.5} ${20 + mouthWidth / 2 - 1},${mouthY}`}
              fill="none" stroke={color} strokeWidth="1.2" strokeLinecap="round" opacity="0.7" />
          )}
        </g>

        {/* Microdose: subtle glow around eyes */}
        {alteredState === 'microdose' && (
          <>
            <circle cx={20 - eyeSpacing} cy={eyeY} r="4.5" fill="none" stroke={color} strokeWidth="0.3" opacity="0.3">
              <animate attributeName="opacity" values="0.2;0.4;0.2" dur="2s" repeatCount="indefinite" />
            </circle>
            <circle cx={20 + eyeSpacing} cy={eyeY} r="4.5" fill="none" stroke={color} strokeWidth="0.3" opacity="0.3">
              <animate attributeName="opacity" values="0.2;0.4;0.2" dur="2s" repeatCount="indefinite" />
            </circle>
          </>
        )}
      </svg>

      <style>{`
        .voice-avatar {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          position: relative;
        }
        .voice-avatar .avatar-body {
          transform-origin: 20px 20px;
        }

        /* Idle breathing */
        .voice-avatar:not(.avatar-speaking):not(.avatar-thinking) .avatar-body {
          animation: avatar-breathe 4s ease-in-out infinite;
        }
        @keyframes avatar-breathe {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.015); }
        }

        /* Speaking bounce */
        .avatar-speaking .avatar-body {
          animation: avatar-speak 0.6s ease-in-out infinite;
        }
        @keyframes avatar-speak {
          0%, 100% { transform: scale(1) translateY(0); }
          25% { transform: scale(1.02) translateY(-0.3px); }
          75% { transform: scale(0.99) translateY(0.3px); }
        }

        /* Thinking pulse */
        .avatar-thinking .avatar-body {
          animation: avatar-think 1.5s ease-in-out infinite;
        }
        @keyframes avatar-think {
          0%, 100% { transform: scale(1); opacity: 1; }
          50% { transform: scale(1.01); opacity: 0.7; }
        }

        /* Drunk sway */
        .avatar-drunk .avatar-body {
          animation: avatar-sway 3s ease-in-out infinite !important;
        }
        @keyframes avatar-sway {
          0%, 100% { transform: rotate(0deg); }
          25% { transform: rotate(2deg); }
          75% { transform: rotate(-2deg); }
        }

        /* Wired jitter */
        .avatar-wired .avatar-body {
          animation: avatar-jitter 0.15s linear infinite !important;
        }
        @keyframes avatar-jitter {
          0% { transform: translate(0, 0); }
          25% { transform: translate(0.3px, -0.3px); }
          50% { transform: translate(-0.3px, 0.3px); }
          75% { transform: translate(0.3px, 0.3px); }
          100% { transform: translate(0, 0); }
        }

        /* Acid glitch */
        .avatar-acid {
          animation: avatar-glitch 5s ease-in-out infinite;
        }
        @keyframes avatar-glitch {
          0%, 94%, 100% { filter: hue-rotate(0deg) saturate(1.8); }
          95% { filter: hue-rotate(90deg) saturate(2.5); transform: translate(1px, 0); }
          96% { filter: hue-rotate(180deg) saturate(2); transform: translate(-1px, 0); }
          97% { filter: hue-rotate(270deg) saturate(2.5); transform: translate(0, 1px); }
        }

        /* Mushrooms dreamy */
        .avatar-mushrooms {
          animation: avatar-dreamy 6s ease-in-out infinite;
        }
        @keyframes avatar-dreamy {
          0%, 100% { filter: hue-rotate(0deg) saturate(1.4); }
          33% { filter: hue-rotate(15deg) saturate(1.6); }
          66% { filter: hue-rotate(-10deg) saturate(1.3); }
        }
      `}</style>
    </div>
  );
}
